// Translate country codes and names to Czech nationality names
export function translateNationality(code?: string): string {
  const val = String(code ?? '');
  if (!val) return '';
  
  // Country code translations (2-letter codes)
  const codeTranslations: Record<string, string> = {
    'CZ': 'Česká republika',
    'SK': 'Slovensko',
    'PL': 'Polsko',
    'DE': 'Německo',
    'AT': 'Rakousko',
    'UA': 'Ukrajina',
    'FR': 'Francie',
    'ES': 'Španělsko',
    'IT': 'Itálie',
    'GB': 'Anglie',
    'UK': 'Velká Británie',
    'US': 'USA',
    'BR': 'Brazílie',
    'AR': 'Argentina',
    'PT': 'Portugalsko',
    'NL': 'Nizozemsko',
    'BE': 'Belgie',
    'CH': 'Švýcarsko',
    'SE': 'Švédsko',
    'NO': 'Norsko',
    'DK': 'Dánsko',
    'FI': 'Finsko',
    'RU': 'Rusko',
    'HR': 'Chorvatsko',
    'RS': 'Srbsko',
    'SI': 'Slovinsko',
    'HU': 'Maďarsko',
    'RO': 'Rumunsko',
    'BG': 'Bulharsko',
    'GR': 'Řecko',
    'TR': 'Turecko',
    'JP': 'Japonsko',
    'CN': 'Čína',
    'KR': 'Jižní Korea',
    'AU': 'Austrálie',
    'NZ': 'Nový Zéland',
    'CA': 'Kanada',
    'MX': 'Mexiko',
    'IE': 'Irsko',
    'IS': 'Island',
  };

  // Full country name translations (from REST Countries API)
  const nameTranslations: Record<string, string> = {
    'Czechia': 'Česká republika',
    'Czech Republic': 'Česká republika',
    'Slovakia': 'Slovensko',
    'Poland': 'Polsko',
    'Germany': 'Německo',
    'Austria': 'Rakousko',
    'Ukraine': 'Ukrajina',
    'France': 'Francie',
    'Spain': 'Španělsko',
    'Italy': 'Itálie',
    'England': 'Anglie',
    'United Kingdom': 'Velká Británie',
    'United States': 'USA',
    'Brazil': 'Brazílie',
    'Argentina': 'Argentina',
    'Portugal': 'Portugalsko',
    'Netherlands': 'Nizozemsko',
    'Belgium': 'Belgie',
    'Switzerland': 'Švýcarsko',
    'Sweden': 'Švédsko',
    'Norway': 'Norsko',
    'Denmark': 'Dánsko',
    'Finland': 'Finsko',
    'Russia': 'Rusko',
    'Croatia': 'Chorvatsko',
    'Serbia': 'Srbsko',
    'Slovenia': 'Slovinsko',
    'Hungary': 'Maďarsko',
    'Romania': 'Rumunsko',
    'Bulgaria': 'Bulharsko',
    'Greece': 'Řecko',
    'Turkey': 'Turecko',
    'Japan': 'Japonsko',
    'China': 'Čína',
    'South Korea': 'Jižní Korea',
    'Australia': 'Austrálie',
    'New Zealand': 'Nový Zéland',
    'Canada': 'Kanada',
    'Mexico': 'Mexiko',
    'Ireland': 'Irsko',
    'Iceland': 'Island',
    'Bosnia and Herzegovina': 'Bosna a Hercegovina',
    'Montenegro': 'Černá Hora',
    'North Macedonia': 'Severní Makedonie',
    'Albania': 'Albánie',
    'Luxembourg': 'Lucembursko',
    'Moldova': 'Moldavsko',
    'Lithuania': 'Litva',
    'Latvia': 'Lotyšsko',
    'Estonia': 'Estonsko',
    'Belarus': 'Bělorusko',
  };

  // Try code translation first (for 2-letter codes)
  const upperCode = val.toUpperCase();
  if (codeTranslations[upperCode]) {
    return codeTranslations[upperCode];
  }

  // Try full name translation (case-insensitive)
  const lowerName = val.toLowerCase();
  for (const [englishName, czechName] of Object.entries(nameTranslations)) {
    if (englishName.toLowerCase() === lowerName) {
      return czechName;
    }
  }

  // Return original if no translation found
  return val;
}

// Convert a 2-letter ISO country code to a flag emoji
export function countryCodeToEmoji(cc?: string): string {
  if (!cc) return '';
  const v = String(cc).trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(v)) return '';
  return v.replace(/./g, (ch) => String.fromCodePoint(127397 + ch.charCodeAt(0)));
}

// Get an emoji flag for a given nationality string (code like "CZ" or English name like "Czechia")
export function getCountryFlag(nationality?: string): string {
  if (!nationality) return '';
  const n = String(nationality).trim();
  if (!n) return '';
  // If already a 2-letter code
  if (/^[A-Za-z]{2}$/.test(n)) {
    // Normalize UK to GB for emoji
    const code = n.toUpperCase() === 'UK' ? 'GB' : n.toUpperCase();
    return countryCodeToEmoji(code);
  }
  // Map of English and Czech names to ISO alpha-2 codes (subset covering our translations)
  const nameToCode: Record<string, string> = {
    'Czechia': 'CZ',
    'Czech Republic': 'CZ',
    'Česká republika': 'CZ',
    'Slovakia': 'SK',
    'Slovensko': 'SK',
    'Poland': 'PL',
    'Polsko': 'PL',
    'Germany': 'DE',
    'Německo': 'DE',
    'Austria': 'AT',
    'Rakousko': 'AT',
    'Ukraine': 'UA',
    'Ukrajina': 'UA',
    'France': 'FR',
    'Francie': 'FR',
    'Spain': 'ES',
    'Španělsko': 'ES',
    'Italy': 'IT',
    'Itálie': 'IT',
    'England': 'GB',
    'United Kingdom': 'GB',
    'Velká Británie': 'GB',
    'United States': 'US',
    'USA': 'US',
    'Brazil': 'BR',
    'Argentina': 'AR',
    'Portugal': 'PT',
    'Netherlands': 'NL',
    'Nizozemsko': 'NL',
    'Belgium': 'BE',
    'Belgie': 'BE',
    'Switzerland': 'CH',
    'Švýcarsko': 'CH',
    'Sweden': 'SE',
    'Švédsko': 'SE',
    'Norway': 'NO',
    'Norsko': 'NO',
    'Denmark': 'DK',
    'Dánsko': 'DK',
    'Finland': 'FI',
    'Finsko': 'FI',
    'Russia': 'RU',
    'Rusko': 'RU',
    'Croatia': 'HR',
    'Chorvatsko': 'HR',
    'Serbia': 'RS',
    'Srbsko': 'RS',
    'Slovenia': 'SI',
    'Slovinsko': 'SI',
    'Hungary': 'HU',
    'Maďarsko': 'HU',
    'Romania': 'RO',
    'Rumunsko': 'RO',
    'Bulgaria': 'BG',
    'Bulharsko': 'BG',
    'Greece': 'GR',
    'Řecko': 'GR',
    'Turkey': 'TR',
    'Turecko': 'TR',
    'Japan': 'JP',
    'Japonsko': 'JP',
    'China': 'CN',
    'Čína': 'CN',
    'South Korea': 'KR',
    'Jižní Korea': 'KR',
    'Australia': 'AU',
    'Austrálie': 'AU',
    'New Zealand': 'NZ',
    'Nový Zéland': 'NZ',
    'Canada': 'CA',
    'Kanada': 'CA',
    'Mexico': 'MX',
    'Mexiko': 'MX',
    'Ireland': 'IE',
    'Irsko': 'IE',
    'Iceland': 'IS',
    'Island': 'IS',
    'Bosnia and Herzegovina': 'BA',
    'Bosna a Hercegovina': 'BA',
    'Montenegro': 'ME',
    'Černá Hora': 'ME',
    'North Macedonia': 'MK',
    'Severní Makedonie': 'MK',
    'Albania': 'AL',
    'Albánie': 'AL',
    'Luxembourg': 'LU',
    'Lucembursko': 'LU',
    'Moldova': 'MD',
    'Moldavsko': 'MD',
    'Lithuania': 'LT',
    'Litva': 'LT',
    'Latvia': 'LV',
    'Lotyšsko': 'LV',
    'Estonia': 'EE',
    'Estonsko': 'EE',
    'Belarus': 'BY',
    'Bělorusko': 'BY',
  };
  const code = nameToCode[n] || nameToCode[n.replace(/\s+/g, ' ').trim()] || nameToCode[n.toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())];
  if (code) return countryCodeToEmoji(code);
  return '';
}
